import React, { useState } from "react";
import { Mail } from "lucide-react";
import * as api from "@/lib/api";
import { useData } from "@/hooks/useData";
import { Badge, Card, EmptyState, Loader, PageHeader, SectionCard } from "@/components/ui";
import { cn } from "@/lib/utils";
import logo from "@/assets/logo.png";

const TYPES = {
  leave_applied: ["Leave applied", "amber"],
  leave_approved: ["Leave approved", "green"],
  leave_rejected: ["Leave rejected", "red"],
  salary_slip: ["Salary slip", "blue"],
  holiday_alert: ["Holiday alert", "violet"],
  document_expiry: ["Document expiry", "red"],
};

const stamp = (iso) => new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

export default function EmailNotifications() {
  const { data: list } = useData(() => api.notifications.list(), []);
  const [selectedId, setSelectedId] = useState(null);

  if (!list) return <Loader />;
  const selected = list.find((e) => e.id === selectedId) || list[0];

  return (
    <>
      <PageHeader description="System emails triggered by leave, payroll and compliance events. No real emails are sent in the demo." />

      {list.length === 0 ? <Card><EmptyState icon={Mail} title="No emails yet" /></Card> : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <SectionCard title="Inbox" count={list.length} className="lg:col-span-2">
            <ul className="space-y-1 max-h-[620px] overflow-y-auto -mx-1 px-1 py-1">
              {list.map((e) => {
                const [label, tone] = TYPES[e.type] || [e.type, "slate"];
                const active = selected?.id === e.id;
                return (
                  <li key={e.id}>
                    <button
                      onClick={() => setSelectedId(e.id)}
                      className={cn("w-full text-left p-3 rounded-2xl group transition-all", active ? "shadow-neu-inset-sm" : "hover:shadow-neu-xs")}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className={cn("text-xs font-bold truncate transition-colors", active ? "text-brand-orange" : "text-brand-blue group-hover:text-brand-orange")}>{e.subject}</p>
                        <span className="text-[10px] text-slate-400 shrink-0">{stamp(e.created_at)}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-1.5">
                        <p className="text-[11px] text-slate-500 truncate">To {e.to}</p>
                        <Badge tone={tone}>{label}</Badge>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </SectionCard>

          <Card className="lg:col-span-3 p-5">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 shrink-0 rounded-xl flex items-center justify-center text-white shadow-neu-brand" style={{ background: "linear-gradient(145deg,#225881,#1a4363)" }}><Mail size={16} /></div>
              <div className="min-w-0">
                <h3 className="text-base font-extrabold text-brand-blue">{selected.subject}</h3>
                <p className="text-[11px] text-slate-500 mt-0.5">From <b>hr@medilearn.ae</b> · To <b>{selected.to}</b> · {stamp(selected.created_at)}</p>
              </div>
            </div>
            <div className="neu-divider my-4" />
            <div className="bg-white rounded-xl border border-slate-200/70 p-6 sm:p-8">
              <img src={logo} alt="MediLearn" className="h-7 w-auto mb-6" />
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{selected.body}</p>
              <p className="text-[11px] text-slate-400 mt-10 pt-4 border-t border-slate-100">MediLearn Tech Skills Training · Business Bay, Dubai · hr@medilearn.ae</p>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
